/**
 * Modal d'ajout d'un compte Telegram utilisateur (MTProto v2).
 *
 * UX inspirée de Telegram Desktop/Web : QR code par défaut, bouton
 * "Utiliser le numéro" pour fallback phone. 2FA gérée en step partagé
 * (même endpoint backend pour les 2 flows — `submit_password`).
 *
 * State machine :
 *   qr-loading → qr-display → poll loop
 *                  ├─→ scanned   → success
 *                  ├─→ needs_2fa → twoFA
 *                  └─→ expired   → restart QR
 *   phone-input → code-input
 *                  ├─→ account   → success
 *                  └─→ needs_2fa → twoFA
 *   twoFA ──→ success
 */

import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, CheckCircle2 } from "@/shared/ui/icons";
import { useQueryClient } from "@tanstack/react-query";

import {
  Button,
  Champ,
  LoadingHint,
  Modal,
  ModalBody,
  ModalFooter,
  Select,
  Vide,
} from "@/shared/ui";
import {
  COUNTRIES as DIAL_COUNTRIES,
  localizedCountryName,
} from "@/shared/countryDialCodes";

import {
  extractTelegramErrorDetail,
  usePollQRLogin,
  useStartPhoneLogin,
  useStartQRLogin,
  useSubmitCode,
  useSubmitPassword,
  type StartQRLoginResponse,
  type TelegramUserAccountResponse,
} from "./api";

// `qrcode.react` (lib SVG du QR de login MTProto) en import dynamique : sans
// ça elle est tirée dans le chunk d'entrée via la chaîne
// CockpitShell → CreatorOnboardingWizard → TelegramConnectStep → ce modal,
// alors qu'elle ne sert QUE sur l'étape "qr-display" de l'ajout d'un compte.
// Lazy → elle quitte le graphe de boot, fetchée seulement quand le QR s'affiche.
const QRCodeSVG = lazy(() =>
  import("qrcode.react").then((m) => ({ default: m.QRCodeSVG })),
);

type Step =
  | "qr-loading"
  | "qr-display"
  | "phone-input"
  | "code-input"
  | "twoFA"
  | "success"
  | "error";

interface Props {
  cyranoId: string;
  onClose: () => void;
}

const QR_POLL_INTERVAL_MS = 2000;

/**
 * Normalise un numero saisi par l'humain vers de l'E.164 strict
 * (`+` puis 8 a 15 chiffres) attendu par le backend. Retire les separateurs
 * courants (espaces, tirets, points, parentheses, slash), convertit un
 * prefixe international `00` en `+`. Retourne `null` si le resultat n'est pas
 * un E.164 valide (ex: numero national sans indicatif pays).
 */
function normalizePhoneE164(raw: string): string | null {
  let v = raw.replace(/[\s.\-()/]/g, "");
  if (v.startsWith("00")) v = "+" + v.slice(2);
  return /^\+\d{8,15}$/.test(v) ? v : null;
}

/** Detecte un 422 (validation backend) quelle que soit la couche HTTP. */
function isValidationError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as Record<string, unknown>;
  const response = (e["response"] ?? {}) as Record<string, unknown>;
  return e["status"] === 422 || response["status"] === 422;
}

interface Country {
  iso: string; // ISO 3166-1 alpha-2 (cle React + value du <select>)
  name: string;
  dial: string; // indicatif, ex "+33"
  // `trunkZero`: le numero national porte un 0 d'acheminement a retirer en
  // format international (FR, GB, DE...). False quand le 0 fait partie du
  // numero international (Italie) ou qu'il n'y a pas de prefixe (US/CA).
  trunkZero: boolean;
}

// `trunkZero=false` : pays sans 0 d'acheminement national a retirer en format
// international — NANP (+1) + plans fermes / sans prefixe trunk (Italie garde
// son 0, Iberie / Nordiques / Latam sans trunk). Pour tous les autres on retire
// un 0 de tete eventuel. Reprend le classement de la liste curatee d'origine
// (FR/DE/GB/MA... = true ; ES/PT/IT/US/BR/MX... = false).
const NO_TRUNK_ZERO = new Set<string>([
  "ES", "PT", "IT", "SM", "VA", "LU", "MC", "AD", "DK", "NO", "IS", "FO",
  "GL", "TN", "BR", "MX",
]);

// Liste COMPLETE (~230 pays, dont la Colombie +57) generee depuis le dataset
// partage `countryDialCodes`. Nom localise FR via `Intl.DisplayNames`, trie par
// nom. Pas d'emoji drapeau : non rendu sous Windows (affiche les 2 lettres ISO).
// France reste preselectionnee via `DEFAULT_COUNTRY_ISO` ci-dessous.
const COUNTRIES: Country[] = DIAL_COUNTRIES.map((c) => ({
  iso: c.iso2,
  name: localizedCountryName(c.iso2, "fr"),
  dial: `+${c.dialCode}`,
  trunkZero: c.dialCode !== "1" && !NO_TRUNK_ZERO.has(c.iso2),
})).sort((a, b) => a.name.localeCompare(b.name, "fr"));

const DEFAULT_COUNTRY_ISO = "FR";

/**
 * Combine le pays choisi + le numero national saisi en E.164.
 * - Si l'utilisateur a colle un numero international complet (`+...` / `00...`),
 *   on l'utilise tel quel (le selecteur devient sans effet, comportement sûr).
 * - Sinon on retire le 0 d'acheminement si le pays l'exige, puis on prefixe
 *   l'indicatif. Retourne `null` si le resultat n'est pas un E.164 valide.
 */
function combineE164(country: Country, national: string): string | null {
  const cleaned = national.replace(/[\s.\-()/]/g, "");
  if (cleaned.startsWith("+") || cleaned.startsWith("00")) {
    return normalizePhoneE164(cleaned);
  }
  let n = cleaned;
  if (country.trunkZero && n.startsWith("0")) n = n.slice(1);
  return normalizePhoneE164(country.dial + n);
}

export function AddTelegramUserAccountModal({ cyranoId, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [step, setStep] = useState<Step>("qr-loading");
  const [authSessionId, setAuthSessionId] = useState<string | null>(null);
  const [qrInfo, setQrInfo] = useState<StartQRLoginResponse | null>(null);
  const [phone, setPhone] = useState("");
  const [countryIso, setCountryIso] = useState(DEFAULT_COUNTRY_ISO);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [account, setAccount] = useState<TelegramUserAccountResponse | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const startQR = useStartQRLogin(cyranoId);
  const pollQR = usePollQRLogin(cyranoId);
  const startPhone = useStartPhoneLogin(cyranoId);
  const submitCode = useSubmitCode(cyranoId);
  const submitPassword = useSubmitPassword(cyranoId);

  const messageForError = useCallback(
    (err: unknown): string => {
      const d = extractTelegramErrorDetail(err);
      if (!d) return t("panels.creators.edit.connection.mtproto.errors.unknown");
      switch (d.code) {
        case "business_bot_required":
          return t("panels.creators.edit.connection.mtproto.botBusinessRequired");
        case "duplicate_account":
          return t("panels.creators.edit.connection.mtproto.errors.duplicate_account");
        case "auth_session_not_found":
          return t("panels.creators.edit.connection.mtproto.errors.auth_session_not_found");
        case "wrong_step":
          return t("panels.creators.edit.connection.mtproto.errors.wrong_step");
        case "creator_id_mismatch":
          return t("panels.creators.edit.connection.mtproto.errors.unknown");
        case "flood_wait":
          return t("panels.creators.edit.connection.mtproto.errors.flood_wait", {
            seconds: d.retry_after_seconds ?? "?",
          });
        case "account_banned":
          return t("panels.creators.edit.connection.mtproto.errors.account_banned");
        case "auth_revoked":
          return t("panels.creators.edit.connection.mtproto.errors.auth_revoked");
        case "network_error":
          return t("panels.creators.edit.connection.mtproto.errors.network_error");
        case "password_needed":
          return t("panels.creators.edit.connection.mtproto.errors.password_needed");
        // Le proxy se pose seul à la connexion (Francis#2742, #2743) : le
        // serveur dit lequel manque, dans quel pays — on lui laisse la parole.
        case "no_free_proxy":
        case "proxy_unusable":
          return (
            d.message ??
            t("panels.creators.edit.connection.mtproto.errors.unknown")
          );
        case "unknown":
        default:
          return t("panels.creators.edit.connection.mtproto.errors.unknown");
      }
    },
    [t],
  );

  // ── Lancer le QR au mount ───────────────────────────────────────────────

  const launchQR = useCallback(async () => {
    setStep("qr-loading");
    setErrorMessage(null);
    try {
      const result = await startQR.mutateAsync();
      setAuthSessionId(result.auth_session_id);
      setQrInfo(result);
      setStep("qr-display");
    } catch (err) {
      setErrorMessage(messageForError(err));
      setStep("error");
    }
  }, [startQR, messageForError]);

  useEffect(() => {
    void launchQR();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Polling QR loop ─────────────────────────────────────────────────────

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (step !== "qr-display" || !authSessionId) return;

    const poll = async () => {
      try {
        const result = await pollQR.mutateAsync(authSessionId);
        if (result.status === "scanned" && result.account) {
          setAccount(result.account);
          setStep("success");
          qc.invalidateQueries({ queryKey: ["telegram-user-accounts", cyranoId] });
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        } else if (result.status === "needs_2fa") {
          setStep("twoFA");
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
        } else if (result.status === "expired") {
          if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
          void launchQR();
        }
      } catch (err) {
        setErrorMessage(messageForError(err));
        setStep("error");
        if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      }
    };

    pollIntervalRef.current = setInterval(poll, QR_POLL_INTERVAL_MS);
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [step, authSessionId, pollQR, messageForError, launchQR, qc, cyranoId]);

  // ── Flow phone ──────────────────────────────────────────────────────────

  async function handleStartPhone() {
    setErrorMessage(null);
    const country =
      COUNTRIES.find((c) => c.iso === countryIso) ?? COUNTRIES[0];
    const normalized = combineE164(country, phone);
    if (!normalized) {
      setErrorMessage(t("panels.creators.edit.connection.mtproto.errors.invalid_phone"));
      return;
    }
    try {
      const result = await startPhone.mutateAsync(normalized);
      setAuthSessionId(result.auth_session_id);
      setStep("code-input");
    } catch (err) {
      setErrorMessage(
        isValidationError(err)
          ? t("panels.creators.edit.connection.mtproto.errors.invalid_phone")
          : messageForError(err),
      );
    }
  }

  async function handleSubmitCode() {
    if (!authSessionId) return;
    setErrorMessage(null);
    try {
      const result = await submitCode.mutateAsync({ asid: authSessionId, code });
      if (result.needs_2fa) {
        setStep("twoFA");
      } else if (result.account) {
        setAccount(result.account);
        setStep("success");
        qc.invalidateQueries({ queryKey: ["telegram-user-accounts", cyranoId] });
      }
    } catch (err) {
      setErrorMessage(messageForError(err));
    }
  }

  async function handleSubmitPassword() {
    if (!authSessionId) return;
    setErrorMessage(null);
    try {
      const result = await submitPassword.mutateAsync({ asid: authSessionId, password });
      setAccount(result);
      setStep("success");
      qc.invalidateQueries({ queryKey: ["telegram-user-accounts", cyranoId] });
    } catch (err) {
      setErrorMessage(messageForError(err));
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title={t("panels.creators.edit.connection.mtproto.addModal.title")}
      closeLabel={t("common.close")}
    >
      {step === "qr-loading" && (
        <ModalBody>
          <div className="flex flex-col items-center gap-3 py-6 text-[12.5px] text-muted">
            <LoadingHint delayMs={0} className="py-0" />
            <span>{t("panels.creators.edit.connection.mtproto.addModal.preparingQr")}</span>
          </div>
        </ModalBody>
      )}

      {step === "qr-display" && qrInfo && (
        <QRStep
          qrInfo={qrInfo}
          onUsePhoneInstead={() => setStep("phone-input")}
          onRegenerate={launchQR}
        />
      )}

      {step === "phone-input" && (
        <PhoneStep
          phone={phone}
          onPhoneChange={setPhone}
          countryIso={countryIso}
          onCountryChange={setCountryIso}
          isPending={startPhone.isPending}
          onSubmit={handleStartPhone}
          onBackToQR={() => {
            setPhone("");
            setErrorMessage(null);
            void launchQR();
          }}
          errorMessage={errorMessage}
        />
      )}

      {step === "code-input" && (
        <CodeStep
          code={code}
          onCodeChange={setCode}
          isPending={submitCode.isPending}
          onSubmit={handleSubmitCode}
          errorMessage={errorMessage}
        />
      )}

      {step === "twoFA" && (
        <TwoFAStep
          password={password}
          onPasswordChange={setPassword}
          isPending={submitPassword.isPending}
          onSubmit={handleSubmitPassword}
          errorMessage={errorMessage}
        />
      )}

      {step === "success" && account && (
        <SuccessStep account={account} onDone={onClose} />
      )}

      {step === "error" && (
        <ErrorStep
          message={errorMessage ?? t("panels.creators.edit.connection.mtproto.errors.unknown")}
          onRetry={() => {
            setErrorMessage(null);
            void launchQR();
          }}
          onClose={onClose}
        />
      )}
    </Modal>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function QRStep({
  qrInfo,
  onUsePhoneInstead,
  onRegenerate,
}: {
  qrInfo: StartQRLoginResponse;
  onUsePhoneInstead: () => void;
  onRegenerate: () => void;
}) {
  const { t } = useTranslation();
  const [secondsLeft, setSecondsLeft] = useState<number>(() => {
    const diff = (new Date(qrInfo.qr_expires_at).getTime() - Date.now()) / 1000;
    return Math.max(0, Math.floor(diff));
  });

  useEffect(() => {
    const id = setInterval(() => {
      const diff = (new Date(qrInfo.qr_expires_at).getTime() - Date.now()) / 1000;
      setSecondsLeft(Math.max(0, Math.floor(diff)));
    }, 1000);
    return () => clearInterval(id);
  }, [qrInfo.qr_expires_at]);

  return (
    <>
      <ModalBody>
        <div className="flex flex-col items-center gap-4">
          <p className="text-center text-[12.5px] text-muted">
            {t("panels.creators.edit.connection.mtproto.addModal.qrInstructions")}
          </p>
          {/* Le QR code se lit sur fond blanc : c'est son rendu, pas un habillage. */}
          <div className="rounded-2xl bg-white p-3">
            <Suspense fallback={<div className="h-48 w-48" />}>
              <QRCodeSVG value={qrInfo.qr_url} size={192} level="M" />
            </Suspense>
          </div>
          <div className="text-[12px] text-faint">
            {secondsLeft > 0 ? (
              t("panels.creators.edit.connection.mtproto.addModal.qrExpiresIn", { seconds: secondsLeft })
            ) : (
              <Button variant="secondary" size="md" onClick={onRegenerate}>
                {t("panels.creators.edit.connection.mtproto.addModal.qrRegenerate")}
              </Button>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" size="lg" onClick={onUsePhoneInstead} className="w-full">
          {t("panels.creators.edit.connection.mtproto.addModal.usePhoneInstead")}
        </Button>
      </ModalFooter>
    </>
  );
}

function PhoneStep({
  phone,
  onPhoneChange,
  countryIso,
  onCountryChange,
  isPending,
  onSubmit,
  onBackToQR,
  errorMessage,
}: {
  phone: string;
  onPhoneChange: (v: string) => void;
  countryIso: string;
  onCountryChange: (v: string) => void;
  isPending: boolean;
  onSubmit: () => void;
  onBackToQR: () => void;
  errorMessage: string | null;
}) {
  const { t } = useTranslation();
  const dial =
    COUNTRIES.find((c) => c.iso === countryIso)?.dial ?? COUNTRIES[0].dial;
  return (
    <>
      <ModalBody className="flex flex-col gap-4">
        <p className="text-[12.5px] text-muted">
          {t("panels.creators.edit.connection.mtproto.addModal.phoneInstructions")}
        </p>
        <Select
          etiquette={t("panels.creators.edit.connection.mtproto.addModal.countryLabel")}
          value={countryIso}
          onChange={(e) => onCountryChange(e.target.value)}
        >
          {COUNTRIES.map((c) => (
            <option key={c.iso} value={c.iso}>
              {c.name} ({c.dial})
            </option>
          ))}
        </Select>
        <Champ
          etiquette={t("panels.creators.edit.connection.mtproto.addModal.phoneLabel")}
          type="tel"
          placeholder="6 12 34 56 78"
          value={phone}
          onChange={(e) => onPhoneChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && phone.trim() && !isPending) onSubmit();
          }}
          autoFocus
          icone={<span className="text-[14px] tabular-nums text-muted">{dial}</span>}
          erreur={errorMessage ?? undefined}
        />
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onBackToQR}>
          {t("panels.creators.edit.connection.mtproto.addModal.backToQR")}
        </Button>
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={isPending || !phone.trim()}
        >
          {isPending
            ? t("panels.creators.edit.connection.mtproto.addModal.sending")
            : t("panels.creators.edit.connection.mtproto.addModal.sendSMS")}
        </Button>
      </ModalFooter>
    </>
  );
}

/**
 * ⏳ Le champ du code reçu reste celui d'avant : c'est un champ « code »
 * (monospace, chiffres) qui attend la famille Saisie de la librairie.
 */
function CodeStep({
  code,
  onCodeChange,
  isPending,
  onSubmit,
  errorMessage,
}: {
  code: string;
  onCodeChange: (v: string) => void;
  isPending: boolean;
  onSubmit: () => void;
  errorMessage: string | null;
}) {
  const { t } = useTranslation();
  return (
    <>
      <ModalBody className="flex flex-col gap-3">
        <p className="text-[12.5px] text-muted">
          {t("panels.creators.edit.connection.mtproto.addModal.codeInstructions")}
        </p>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-medium uppercase tracking-wider text-faint">
            {t("panels.creators.edit.connection.mtproto.addModal.codeLabel")}
          </label>
          <input
            type="text"
            inputMode="numeric"
            placeholder="12345"
            value={code}
            onChange={(e) => onCodeChange(e.target.value)}
            autoFocus
            className="border border-line bg-canvas px-2.5 py-1.5 font-mono text-sm text-fg focus:border-line-strong focus:outline-none"
          />
        </div>
        {errorMessage && <ErrorMessage message={errorMessage} />}
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={isPending || !code.trim()}
          className="w-full"
        >
          {isPending
            ? t("panels.creators.edit.connection.mtproto.addModal.verifying")
            : t("panels.creators.edit.connection.mtproto.addModal.verifyCode")}
        </Button>
      </ModalFooter>
    </>
  );
}

/**
 * ⏳ Le champ du mot de passe 2FA reste celui d'avant : c'est un champ SECRET,
 * qui attend la famille Saisie de la librairie.
 */
function TwoFAStep({
  password,
  onPasswordChange,
  isPending,
  onSubmit,
  errorMessage,
}: {
  password: string;
  onPasswordChange: (v: string) => void;
  isPending: boolean;
  onSubmit: () => void;
  errorMessage: string | null;
}) {
  const { t } = useTranslation();
  return (
    <>
      <ModalBody className="flex flex-col gap-3">
        <p className="text-[12.5px] text-muted">
          {t("panels.creators.edit.connection.mtproto.addModal.twoFAInstructions")}
        </p>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] font-medium uppercase tracking-wider text-faint">
            {t("panels.creators.edit.connection.mtproto.addModal.twoFALabel")}
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            autoFocus
            className="border border-line bg-canvas px-2.5 py-1.5 text-sm text-fg focus:border-line-strong focus:outline-none"
          />
        </div>
        {errorMessage && <ErrorMessage message={errorMessage} />}
      </ModalBody>
      <ModalFooter>
        <Button
          variant="primary"
          onClick={onSubmit}
          disabled={isPending || !password}
          className="w-full"
        >
          {isPending
            ? t("panels.creators.edit.connection.mtproto.addModal.verifying")
            : t("panels.creators.edit.connection.mtproto.addModal.verify2FA")}
        </Button>
      </ModalFooter>
    </>
  );
}

function SuccessStep({
  account,
  onDone,
}: {
  account: TelegramUserAccountResponse;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const name =
    [account.first_name, account.last_name].filter(Boolean).join(" ") ||
    account.tg_username ||
    t("panels.creators.edit.connection.mtproto.unnamedAccount");
  return (
    <>
      <ModalBody>
        <Vide
          icone={<CheckCircle2 />}
          titre={t("panels.creators.edit.connection.mtproto.addModal.successTitle")}
          texte={t("panels.creators.edit.connection.mtproto.addModal.successMessage", { name })}
        />
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" size="lg" onClick={onDone} className="w-full">
          {t("panels.creators.edit.connection.mtproto.addModal.done")}
        </Button>
      </ModalFooter>
    </>
  );
}

function ErrorStep({
  message,
  onRetry,
  onClose,
}: {
  message: string;
  onRetry: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <ModalBody>
        <Vide taille="sm" icone={<AlertCircle />} titre={message} />
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>
          {t("panels.creators.edit.connection.mtproto.addModal.cancel")}
        </Button>
        <Button variant="primary" onClick={onRetry}>
          {t("panels.creators.edit.connection.mtproto.addModal.retry")}
        </Button>
      </ModalFooter>
    </>
  );
}

/** L'erreur sous un champ : du texte en rouge de sens, pas un bandeau plein. */
function ErrorMessage({ message }: { message: string }) {
  return (
    <p role="alert" className="text-[12.5px] text-erreur">
      {message}
    </p>
  );
}
